// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a block of raw HTML in a Markdown document, as READMEs use for a centred logo and a row of
// badges (`<p align="center"><img src="docs/logo.png" width="420"></p>`). Konvo draws it itself,
// never as HTML: a short list of text and layout tags becomes React elements with only the
// attributes below, pictures load through the document's source (DocumentPicture) and open in the
// media viewer unless they are a link, links open in a new tab, and everything else (scripts,
// styles, frames, forms, handlers) is left out.

import {DocumentPicture, type MarkdownPictureLoader} from '@app/features/document_preview/DocumentPicture';
import styles from '@app/features/document_preview/DocumentPreview.module.css';
import {pictureSize} from '@app/features/document_preview/DocumentPreviewUtils';
import React, {useMemo} from 'react';

/** Tags drawn as themselves (or the tag given). Others show only what they hold. */
const TAGS: Record<string, string> = {
	p: 'p',
	div: 'div',
	center: 'div',
	section: 'div',
	figure: 'div',
	figcaption: 'div',
	details: 'details',
	summary: 'summary',
	span: 'span',
	picture: 'span',
	strong: 'strong',
	b: 'strong',
	em: 'em',
	i: 'em',
	u: 'u',
	s: 's',
	del: 's',
	small: 'small',
	code: 'code',
	kbd: 'kbd',
	sub: 'sub',
	sup: 'sup',
	br: 'br',
	hr: 'hr',
	ul: 'ul',
	ol: 'ol',
	li: 'li',
	blockquote: 'blockquote',
	table: 'table',
	thead: 'thead',
	tbody: 'tbody',
	tr: 'tr',
	td: 'td',
	th: 'th',
};
/** Tags left out with everything inside them. */
const DROPPED = new Set([
	'script',
	'style',
	'template',
	'noscript',
	'iframe',
	'frame',
	'object',
	'embed',
	'form',
	'input',
	'button',
	'select',
	'textarea',
	'svg',
	'math',
	'canvas',
	'video',
	'audio',
	'source',
	'track',
	'link',
	'meta',
	'title',
	'head',
]);
const HEADINGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
const STRUCTURE = new Set(['table', 'thead', 'tbody', 'tr', 'ul', 'ol']);
const ALIGN = new Set(['left', 'center', 'right', 'justify']);

/** A link the document may open: on the web or an email address. */
function safeHref(value: string | null): string | undefined {
	const href = value?.trim();
	return href && /^(https?:|mailto:)/i.test(href) ? href : undefined;
}

function alignOf(element: Element, tag: string): React.CSSProperties | undefined {
	const align = (element.getAttribute('align') ?? (tag === 'center' ? 'center' : '')).toLowerCase();
	return ALIGN.has(align) ? {textAlign: align as React.CSSProperties['textAlign']} : undefined;
}

/** How the block's pictures load and whether a tap opens them; `linked` inside a link, which wins. */
interface PictureOptions {
	load?: MarkdownPictureLoader;
	open: boolean;
	linked?: boolean;
}

function toReact(node: Node, key: number, pictures: PictureOptions): React.ReactNode {
	if (node.nodeType === Node.TEXT_NODE) {
		const text = node.textContent?.replace(/\s+/g, ' ') ?? '';
		// Tables and lists hold no text of their own.
		return text === ' ' && STRUCTURE.has(node.parentElement?.tagName.toLowerCase() ?? '') ? null : text;
	}
	if (node.nodeType !== Node.ELEMENT_NODE) return null;
	const element = node as Element;
	const tag = element.tagName.toLowerCase();
	if (DROPPED.has(tag)) return null;
	const children = (options = pictures) =>
		Array.from(element.childNodes, (child, index) => toReact(child, index, options));
	if (tag === 'img') {
		const src = element.getAttribute('src')?.trim();
		if (!src) return null;
		return (
			<DocumentPicture
				key={key}
				src={src}
				alt={element.getAttribute('alt') ?? ''}
				width={pictureSize(element.getAttribute('width'))}
				height={pictureSize(element.getAttribute('height'))}
				load={pictures.load}
				open={pictures.open && !pictures.linked}
			/>
		);
	}
	if (tag === 'a') {
		const href = safeHref(element.getAttribute('href'));
		if (!href) return <React.Fragment key={key}>{children()}</React.Fragment>;
		return (
			<a
				key={key}
				href={href}
				target="_blank"
				rel="noreferrer noopener"
				title={element.getAttribute('title') ?? undefined}
			>
				{children({...pictures, linked: true})}
			</a>
		);
	}
	// Headings stay out of the outline (it counts the document's # headings), so they're paragraphs.
	if (HEADINGS.has(tag)) {
		const level = Number(tag.slice(1));
		return (
			<p
				key={key}
				className={styles.htmlHeading}
				data-level={Math.min(level, 4)}
				role="heading"
				aria-level={level}
				style={alignOf(element, tag)}
			>
				{children()}
			</p>
		);
	}
	const as = TAGS[tag];
	if (!as) return <React.Fragment key={key}>{children()}</React.Fragment>;
	if (as === 'br' || as === 'hr') return React.createElement(as, {key});
	// Folded unless it says open, as on GitHub.
	if (as === 'details')
		return React.createElement(
			'details',
			{key, className: styles.details, open: element.hasAttribute('open') || undefined},
			...children(),
		);
	return React.createElement(as, {key, style: alignOf(element, tag)}, ...children());
}

export function MarkdownHtml({
	html,
	load,
	openPictures = false,
}: {
	html: string;
	load?: MarkdownPictureLoader;
	openPictures?: boolean;
}) {
	const body = useMemo(() => new DOMParser().parseFromString(html, 'text/html').body, [html]);
	return (
		<div className={styles.html} data-flx="document-preview.markdown.html">
			{Array.from(body.childNodes, (child, index) => toReact(child, index, {load, open: openPictures}))}
		</div>
	);
}
